import React, { useRef, useState } from "react";
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButton, IonCard, IonCardContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const emojis = ["🍎", "🎈", "🚗", "🐶", "🦄", "🏀", "🍪", "🐟", "🍇", "🌻"];

const getRandomNumber = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

const generateQuestion = () => {
    const num1 = getRandomNumber(3, 9);
    const num2 = getRandomNumber(1, num1 - 1); // Ensure num2 is smaller than num1
    const correctAnswer = num1 - num2;
    const randomEmoji = emojis[Math.floor(Math.random() * emojis.length)];

    const options = new Set<number>();
    options.add(correctAnswer);
    while (options.size < 3) {
        options.add(getRandomNumber(0, 9));
    }

    return {
        question: `${num1} - ${num2} = ?`,
        correctAnswer,
        options: Array.from(options).sort(() => Math.random() - 0.5), // Shuffle options
        emoji: randomEmoji,
        num1,
        num2,
    };
};

const PictureSubtraction: React.FC = () => {
    const [quiz, setQuiz] = useState(generateQuestion());
    const [feedback, setFeedback] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);
    document.title = "Picture Subtraction for UKG | Visual Subtraction Activities & Worksheets";

    const handleAnswerClick = (option: number) => {
        if (option === quiz.correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                setQuiz(generateQuestion())
                setShowImage(false);
                setFeedback("")
            }, 5000)
        } else {
            setShowImage(false);
            setFeedback("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach subtraction using pictures! Fun and free picture subtraction worksheets for UKG kids to develop early math skills through visuals and storytelling." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/substraction/picture-substraction" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Picture Subtraction</h1>

                                    <p>
                                        Subtraction becomes simple when children can see it happening. With <strong>Picture Subtraction</strong>, UKG kids use images like fruits, animals, or objects to understand the concept of taking away. This visual approach helps build a strong math foundation in a fun and interactive way.
                                    </p>

                                    <h2>🎯 What Is Picture Subtraction?</h2>
                                    <p>
                                        Picture subtraction means subtracting by <strong>crossing out, covering, or removing</strong> objects shown in pictures. Children learn subtraction as “taking away” from a group, using visual cues.
                                    </p>

                                    <h2>🧠 Learning Objectives</h2>
                                    <ul>
                                        <li>Understand subtraction as <strong>“taking away”</strong></li>
                                        <li>Count remaining objects after removal</li>
                                        <li>Visualize and relate math to real-life examples</li>
                                        <li>Improve observation and early arithmetic skills</li>
                                    </ul>

                                    <h2>🔢 Examples of Picture Subtraction</h2>
                                    <p><strong>Example 1:</strong> 🐱🐱🐱 − 🐱 = <strong>2 cats left</strong></p>
                                    <p><strong>Example 2:</strong> 🍎🍎🍎🍎 − 🍎🍎 = <strong>2 apples left</strong></p>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/smallest-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Smallest Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/biggest-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Biggest Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/number-comparison"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Number Comparison</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🏡 How to Teach Picture Subtraction at Home</h2>
                                    <ul>
                                        <li>Use toys, blocks, or snacks to show subtraction live</li>
                                        <li>Ask: <strong>“You had 5 biscuits, you ate 2. How many are left?”</strong></li>
                                        <li>Draw objects and help your child cross out the ones being subtracted</li>
                                        <li>Make it playful and real – include characters or stories</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Picture Subtraction</h1>

                                <p>
                                    Subtraction becomes simple when children can see it happening. With <strong>Picture Subtraction</strong>, UKG kids use images like fruits, animals, or objects to understand the concept of taking away. This visual approach helps build a strong math foundation in a fun and interactive way.
                                </p>

                                <h2>🎯 What Is Picture Subtraction?</h2>
                                <p>
                                    Picture subtraction means subtracting by <strong>crossing out, covering, or removing</strong> objects shown in pictures. Children learn subtraction as “taking away” from a group, using visual cues.
                                </p>

                                <h2>🧠 Learning Objectives</h2>
                                <ul>
                                    <li>Understand subtraction as <strong>“taking away”</strong></li>
                                    <li>Count remaining objects after removal</li>
                                    <li>Visualize and relate math to real-life examples</li>
                                    <li>Improve observation and early arithmetic skills</li>
                                </ul>

                                <h2>🔢 Examples of Picture Subtraction</h2>
                                <p><strong>Example 1:</strong> 🐱🐱🐱 − 🐱 = <strong>2 cats left</strong></p>
                                <p><strong>Example 2:</strong> 🍎🍎🍎🍎 − 🍎🍎 = <strong>2 apples left</strong></p>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/smallest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Smallest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/biggest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Biggest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/number-comparison"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Number Comparison</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🏡 How to Teach Picture Subtraction at Home</h2>
                                <ul>
                                    <li>Use toys, blocks, or snacks to show subtraction live</li>
                                    <li>Ask: <strong>“You had 5 biscuits, you ate 2. How many are left?”</strong></li>
                                    <li>Draw objects and help your child cross out the ones being subtracted</li>
                                    <li>Make it playful and real – include characters or stories</li>
                                </ul>
                            </article>
                            <h2>How many are left?</h2>
                            <div style={{ fontSize: "2rem", marginBottom: "10px" }}>
                                {Array(quiz.num1).fill(quiz.emoji).join(" ")}
                            </div>
                            <h3>−</h3>
                            <div style={{ fontSize: "2rem", marginBottom: "10px" }}>
                                {Array(quiz.num2).fill(quiz.emoji).join(" ")}
                            </div>
                            <h3>= ?</h3>
                            <div style={{ marginBottom: "20px", display: 'flex', flexWrap: 'wrap' }}>
                                {quiz.options.map((option, index) => (
                                    <div className="background-blue" key={index} onClick={() => handleAnswerClick(option)} style={{ margin: "5px", padding: "10px", width: "60px" }}>
                                        {option}
                                    </div>
                                ))}
                            </div>
                            {feedback && <p>{feedback}</p>}
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default PictureSubtraction;
